/**
 * PrintView — printable letter-paper modal for the Team Members roster.
 *
 * Driven by a column-config list (see ../columns.tsx). The consumer assembles
 * the right columns + members for the chosen scope ('current' = WYSIWYG;
 * 'full' = all data, all columns) and passes them in — this component is
 * scope-agnostic.
 *
 * Header / footer / filter display come from the shared ReportChrome so this
 * report matches the Equipment Temperatures report. No divider rules —
 * spacing only.
 *
 * Layout notes:
 *   - Header subtitle is "{Store Name} · {Chain Name}"; header-right shows
 *     the report generation date.
 *   - "Store Information" section (Customer / Store / Address) on page 1 only
 *     — cover-sheet pattern, mirrors the Live Status equipment print report.
 *   - "Employee Records" section, page 1 only, directly above the table —
 *     holds the current-view filter description.
 *   - Footer is a single centered "Generated at {date/time} · {name}" line;
 *     Page X of Y is the only thing on the right.
 *   - Print filename matches the Excel export's naming convention via
 *     `printWithFileName` (browsers use document.title as the suggested
 *     "Save as PDF" filename).
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Box, HStack, Modal } from 'native-base';
import { Button, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import type { ColumnContext, ColumnDef } from './team-management-columns';
import type { TeamMember } from '../types';
import {
  REPORT_CHROME_STYLES,
  ReportFooter,
  ReportHeader,
  ReportSectionBar,
  ReportStoreInfo,
  printWithFileName,
} from './team-management-report-chrome';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  members: TeamMember[];
  columns: ColumnDef[];
  columnContext: ColumnContext;
  resolveLabel: (key: string) => string;
  siteName: string;
  chainName: string; // brand/chain name — shown in the header subtitle after the store name
  reportTitle: string; // e.g. "Team Roster" or "User Access Report"
  filterDescription: string; // the current-view filter description — shown in the "Employee Records" section, above the table
  brandLogoUrl?: string; // header logo
  // Store Information section (Customer / Store / Address only)
  customerName: string;
  address: string;
  storeInfoLabel: string;
  customerLabel: string;
  storeLabel: string;
  addressLabel: string;
  // "Employee Records" section — sits between Store Information and the
  // table, holds the current-view filter description.
  employeeRecordsLabel: string;
  filtersLabel: string;
  // Footer — "Generated at {date/time} · {name}", single centered line
  generatedAtLabel: string; // "Generated at"
  generatedByName: string;
  fileName: string; // pre-built via ReportChrome.buildExportFileName — matches the Excel export
  emptyMessage: string;
  closeLabel: string;
  printLabel: string;
};

// Rows per letter-paper sheet (landscape).
const ROWS_PER_PAGE = 12;

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const PRINT_STYLES = `
  .team-report-screen-wrap {
    background: #f3f4f6;
    padding: 24px 0;
    overflow-y: auto;
    max-height: calc(90vh - 56px);
  }
  .team-report-paper {
    width: 11in;
    min-height: 8.5in;
    margin: 0 auto;
    background: white;
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.12);
    padding: 0.5in 0.5in;
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .team-report-paper + .team-report-paper { margin-top: 24px; }
  .team-report-store-info { margin-bottom: 10px; }
  .team-report-section { margin-bottom: 10px; }
  /* Matches .rpt-store-grid's 10px left padding — this section reuses the
     rpt-store-cell-* classes directly instead of the grid wrapper, so it
     needs the same padding applied explicitly or its label/value sit
     flush-left under the indented section bar. */
  .team-report-filter-line { padding: 0 10px; }
  .team-report-table {
    width: 100%;
    border-collapse: collapse;
    flex: 0 0 auto;
    /* Fixed layout — the browser would otherwise size each page's <table>
       from ITS OWN rows' content, so the same column could land at a
       different width/x-position on page 1 vs. page 2. Fixed layout makes
       every page honor the <th> widths below exactly. */
    table-layout: fixed;
  }
  .team-report-spacer {
    flex: 1 1 auto;
  }
  .team-report-table th {
    text-align: left;
    font-size: 9.5px;
    color: #374151;
    background: #f3f4f6;
    padding: 5px 6px;
    border-bottom: 1px solid #d1d5db;
    font-weight: 600;
  }
  .team-report-table td {
    font-size: 10.5px;
    color: #111827;
    padding: 6px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: top;
    /* table-layout: fixed no longer lets a long value push the column wider —
       wrap instead of overflowing into the next cell. */
    overflow-wrap: break-word;
  }
  .team-report-table tr:nth-child(even) td { background: #fafafa; }
  .team-report-cell-bold { font-weight: 600; }
  .team-report-cell-muted { color: #9ca3af; }

  @media print {
    @page { size: letter landscape; margin: 0; }
    html, body {
      background: white !important;
      margin: 0 !important;
      height: auto !important;
      overflow: visible !important;
    }
    body > *:not(.team-report-print-modal) { display: none !important; }
    .team-report-print-modal {
      display: block !important;
      position: static !important;
      width: 100% !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      background: white !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .team-report-print-chrome { display: none !important; }
    .team-report-screen-wrap {
      padding: 0 !important;
      background: white !important;
      overflow: visible !important;
      max-height: none !important;
    }
    .team-report-paper {
      box-shadow: none !important;
      margin: 0 !important;
      /* .team-report-paper is overflow: hidden on screen (keeps the
         rounded-corner shadow card clean), but that same rule in print would
         silently clip any content that doesn't fit within 8.5in instead of
         letting it flow onto a new printed page. Visible here so an
         imperfect row-height estimate degrades to an extra page instead of
         silently missing rows. */
      overflow: visible !important;
    }
    .team-report-paper + .team-report-paper {
      margin-top: 0 !important;
      page-break-before: always;
      break-before: page;
    }
  }
`;

const Paper: React.FC<{
  rows: TeamMember[];
  columns: ColumnDef[];
  columnContext: ColumnContext;
  resolveLabel: (key: string) => string;
  pageIndex: number;
  totalPages: number;
  isFirstPage: boolean;
  footerCenterText: string;
  siteName: string;
  chainName: string;
  reportDateText: string;
  reportTitle: string;
  brandLogoUrl?: string;
  storeInfo: {
    storeInfoLabel: string;
    customerLabel: string;
    customerValue: string;
    storeLabel: string;
    storeValue: string;
    addressLabel: string;
    addressValue: string;
  };
  employeeRecordsLabel: string;
  filtersLabel: string;
  filterDescription: string;
}> = ({
  rows,
  columns,
  columnContext,
  resolveLabel,
  pageIndex,
  totalPages,
  isFirstPage,
  footerCenterText,
  siteName,
  chainName,
  reportDateText,
  reportTitle,
  brandLogoUrl,
  storeInfo,
  employeeRecordsLabel,
  filtersLabel,
  filterDescription,
}) => {
  const { t } = useTranslation();
  // Percentage widths, not fixed px — table-layout: fixed does NOT shrink
  // over-specified column widths to fit; it renders the table at (at least)
  // the sum of the column widths, so a fixed-px <th> width causes real
  // horizontal overflow the moment the column set's total exceeds the page's
  // usable width. Percentages always sum to exactly 100% of the table's
  // actual width, regardless of how many/which columns are selected.
  //
  // `colWidth` falls back to a default — every print-visible column is
  // expected to declare `width` in columns.tsx; falling back to a sane
  // default here means a column missing `width` degrades to "reasonably
  // sized" instead of "invisible."
  const PRINT_COL_FALLBACK_WIDTH = 120;
  const colWidth = (c: ColumnDef) => c.width ?? PRINT_COL_FALLBACK_WIDTH;
  const columnWidthTotal = columns.reduce((sum, c) => sum + colWidth(c), 0) || 1;
  return (
    <div className="team-report-paper">
      <ReportHeader logoUrl={brandLogoUrl} title={reportTitle} subtitle={`${siteName} · ${chainName}`} rightLines={[reportDateText]} />
      {isFirstPage && (
        <>
          <div className="team-report-store-info">
            <ReportSectionBar>{storeInfo.storeInfoLabel}</ReportSectionBar>
            <ReportStoreInfo
              customerLabel={storeInfo.customerLabel}
              customerValue={storeInfo.customerValue}
              storeLabel={storeInfo.storeLabel}
              storeValue={storeInfo.storeValue}
              addressLabel={storeInfo.addressLabel}
              addressValue={storeInfo.addressValue}
            />
          </div>
          {/* "Employee Records" section — holds the current-view filter
              description, above the table. */}
          <div className="team-report-section">
            <ReportSectionBar>{employeeRecordsLabel}</ReportSectionBar>
            <div className="team-report-filter-line">
              <div className="rpt-store-cell-label">{filtersLabel}</div>
              <div className="rpt-store-cell-value">{filterDescription}</div>
            </div>
          </div>
        </>
      )}
      <table className="team-report-table">
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.id} style={{ width: `${(colWidth(c) / columnWidthTotal) * 100}%` }}>
                {resolveLabel(c.labelKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(m => (
            <tr key={m.id}>
              {columns.map((c, ci) => {
                // Icon-composite status columns render their print cell (plain
                // text — see EmploymentStatusPrintCell/UserStatusPrintCell in
                // columns.tsx); everything else prints plain text too.
                if (c.renderPrintCell) {
                  return (
                    <td key={c.id} className={ci === 0 ? 'team-report-cell-bold' : ''}>
                      {c.renderPrintCell(m, columnContext)}
                    </td>
                  );
                }
                const text = c.renderText(m, columnContext);
                const cls = ci === 0 ? 'team-report-cell-bold' : text ? '' : 'team-report-cell-muted';
                return (
                  <td key={c.id} className={cls}>
                    {text || '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Fills leftover page height so the footer stays pinned to the bottom
          without stretching the table's own rows (a lightly-filtered view
          with only 1-2 rows would otherwise get rows stretched to half the
          page tall). Row height tracks its own content naturally. */}
      <div className="team-report-spacer" />
      <ReportFooter
        centerText={footerCenterText}
        pageLabel={t('store_settings:report_page_of', { current: pageIndex + 1, total: totalPages })}
      />
    </div>
  );
};

export const PrintView: React.FC<Props> = ({
  isOpen,
  onClose,
  members,
  columns,
  columnContext,
  resolveLabel,
  siteName,
  chainName,
  reportTitle,
  filterDescription,
  brandLogoUrl,
  customerName,
  address,
  storeInfoLabel,
  customerLabel,
  storeLabel,
  addressLabel,
  employeeRecordsLabel,
  filtersLabel,
  generatedAtLabel,
  generatedByName,
  fileName,
  emptyMessage,
  closeLabel,
  printLabel,
}) => {
  const { t } = useTranslation();
  const { locale } = useAppLocale();
  // Reparent the print-modal div to <body> before window.print() — defeats
  // native-base Modal's viewport clipping. Restore on afterprint. Also sets
  // document.title to the export filename for the duration of the print, so
  // "Save as PDF" suggests the same name as the Excel export.
  const handlePrint = () => {
    const node = document.querySelector('.team-report-print-modal');
    if (!node || !node.parentNode) {
      printWithFileName(fileName, () => window.print());
      return;
    }
    const placeholder = document.createComment('print-modal-placeholder');
    node.parentNode.insertBefore(placeholder, node);
    document.body.appendChild(node);
    const restore = () => {
      window.removeEventListener('afterprint', restore);
      if (placeholder.parentNode) {
        placeholder.parentNode.insertBefore(node, placeholder);
        placeholder.parentNode.removeChild(placeholder);
      }
    };
    window.addEventListener('afterprint', restore);
    printWithFileName(fileName, () => window.print());
  };

  useEffect(() => {
    const noop = () => {};
    window.addEventListener('afterprint', noop);
    return () => window.removeEventListener('afterprint', noop);
  }, []);

  const now = moment().locale(locale);
  const dateTimeText = now.format('MMM D, YYYY h:mm A');
  // Header-right shows just the date (no time) — the full date/time lives in
  // the footer's "Generated at" line instead.
  const reportDateText = now.format('MMM D, YYYY');
  // Footer center — single line: "Generated at {date/time} · {name}".
  const footerCenterText = generatedByName
    ? `${generatedAtLabel} ${dateTimeText} · ${generatedByName}`
    : `${generatedAtLabel} ${dateTimeText}`;

  const pages = chunk(members, ROWS_PER_PAGE);
  const totalPages = Math.max(pages.length, 1);

  const storeInfo = {
    storeInfoLabel,
    customerLabel,
    customerValue: customerName,
    storeLabel,
    storeValue: siteName,
    addressLabel,
    addressValue: address,
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES + REPORT_CHROME_STYLES }} />
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <Modal.Content maxW="95%" maxH="95%">
          <div className="team-report-print-modal">
            {/* native-base's Box props don't declare `className`, but native-base-web forwards
                unknown props to the underlying DOM node — needed here so the `@media print`
                selector in PRINT_STYLES can hide this on-screen-only chrome bar. */}
            <Box
              {...({ className: 'team-report-print-chrome' } as any)}
              px={4}
              py={3}
              bg="white"
              borderBottomWidth={1}
              borderColor="gray.200"
            >
              <HStack alignItems="center" justifyContent="space-between">
                <Text size="md" fontWeight={600} color="gray.900">
                  {reportTitle} · {siteName}
                </Text>
                <HStack space={2}>
                  <Button alternate outline size="sm" onPress={onClose}>
                    {closeLabel}
                  </Button>
                  <Button size="sm" onPress={handlePrint}>
                    {printLabel}
                  </Button>
                </HStack>
              </HStack>
            </Box>
            <div className="team-report-screen-wrap">
              {pages.map((rows, i) => (
                <Paper
                  key={i}
                  rows={rows}
                  columns={columns}
                  columnContext={columnContext}
                  resolveLabel={resolveLabel}
                  pageIndex={i}
                  totalPages={totalPages}
                  isFirstPage={i === 0}
                  footerCenterText={footerCenterText}
                  siteName={siteName}
                  chainName={chainName}
                  reportDateText={reportDateText}
                  reportTitle={reportTitle}
                  brandLogoUrl={brandLogoUrl}
                  storeInfo={storeInfo}
                  employeeRecordsLabel={employeeRecordsLabel}
                  filtersLabel={filtersLabel}
                  filterDescription={filterDescription}
                />
              ))}
              {pages.length === 0 && (
                <div className="team-report-paper">
                  <ReportHeader
                    logoUrl={brandLogoUrl}
                    title={reportTitle}
                    subtitle={`${siteName} · ${chainName}`}
                    rightLines={[reportDateText]}
                  />
                  <div className="team-report-store-info">
                    <ReportSectionBar>{storeInfoLabel}</ReportSectionBar>
                    <ReportStoreInfo
                      customerLabel={customerLabel}
                      customerValue={customerName}
                      storeLabel={storeLabel}
                      storeValue={siteName}
                      addressLabel={addressLabel}
                      addressValue={address}
                    />
                  </div>
                  <div className="team-report-section">
                    <ReportSectionBar>{employeeRecordsLabel}</ReportSectionBar>
                    <div className="team-report-filter-line">
                      <div className="rpt-store-cell-label">{filtersLabel}</div>
                      <div className="rpt-store-cell-value">{filterDescription}</div>
                    </div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                    {emptyMessage}
                  </div>
                  <ReportFooter centerText={footerCenterText} pageLabel={t('store_settings:report_page_of', { current: 1, total: 1 })} />
                </div>
              )}
            </div>
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
};
